import { memo } from 'react';
import { useHistory } from 'react-router-dom';
import { FolderModalTypes } from 'domains/core/components/folders/types';
import MoreOptionsDropdown from 'domains/core/components/MoreOptionsDropdown';
import { Segment, SegmentTableItemType } from 'domains/segments/types';
import useSegmentQueryManager from 'hooks/useSegmentQueryManager';
import { Folder } from 'models/types';
import { URLPaths } from 'models/enums';
import { getSegmentCreatePath, hasInvalidRelativeDates } from 'domains/segments/utils';
import { removeTypeFromId } from 'utils/folders';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';

const allContactsRowName = 'All Contacts';

export type SegmentsTableOptionsCellProps = {
    handleModalOpen: (item: Segment | Folder, modalType: FolderModalTypes) => void;
    isDownloadEnabled: boolean;
    setAudienceToDownload: (segment: Segment) => void;
    value: any;
};

/**
 * A memoized component that renders the options dropdown cell for each segment/folder row.
 */
const SegmentsTableOptionsCell = ({
    handleModalOpen,
    isDownloadEnabled,
    setAudienceToDownload,
    value,
}: SegmentsTableOptionsCellProps) => {
    const history = useHistory();
    const { changeViewParams, folderId } = useSegmentQueryManager();
    const { canManageAudiences, canViewAudienceContacts, canDownloadAudienceContacts } = useAccessCentrePermissions();

    const rowData = {
        ...value.row.original,
        id: removeTypeFromId(value.row.original.id),
    };

    const isDeleteEnabled = rowData.canDelete;

    const deleteOption = {
        name: 'Delete',
        onClick: () => handleModalOpen(rowData, FolderModalTypes.DELETE),
    };

    const archiveOption = {
        name: 'Archive',
        onClick: () => handleModalOpen(rowData, FolderModalTypes.ARCHIVE),
    };

    const unarchiveOption = {
        name: 'Unarchive',
        onClick: () => handleModalOpen(rowData, FolderModalTypes.UNARCHIVE),
    };

    const segmentMenuItems = [
        ...(canViewAudienceContacts
            ? [
                  {
                      name: 'View Contacts',
                      onClick: () => {
                          history.push(URLPaths.SEGMENTS_CONTACTS, {
                              segmentId: rowData.id,
                              segmentName: rowData.name,
                          });
                      },
                  },
              ]
            : []),
        ...(canManageAudiences
            ? [
                  {
                      disabled: hasInvalidRelativeDates(value.row.values),
                      name: 'Copy Audience Filters',
                      onClick: () => {
                          history.push(getSegmentCreatePath(folderId), {
                              segmentDefinition: rowData.segmentDefinition,
                              parentFolderId: folderId,
                          });
                      },
                  },
              ]
            : []),
        ...(canManageAudiences && !rowData.isArchived
            ? [
                  {
                      name: 'Move',
                      onClick: () => handleModalOpen(rowData, FolderModalTypes.MOVE),
                  },
              ]
            : []),
        ...(canDownloadAudienceContacts && isDownloadEnabled
            ? [
                  {
                      name: 'Download Contacts',
                      onClick: () => setAudienceToDownload(rowData),
                  },
              ]
            : []),
        ...(canManageAudiences && rowData.name !== allContactsRowName
            ? rowData.isArchived
                ? [unarchiveOption]
                : [archiveOption]
            : []),
        ...(canManageAudiences && isDeleteEnabled ? [deleteOption] : []),
    ];

    const folderMenuItems = [
        {
            name: 'Open',
            onClick: () => changeViewParams({ newFolderId: rowData.id }),
        },
        ...(canManageAudiences
            ? [
                  {
                      name: 'Rename',
                      onClick: () => handleModalOpen(rowData, FolderModalTypes.RENAME),
                  },
                  ...(isDeleteEnabled ? [deleteOption] : []),
              ]
            : []),
    ];

    const menuItems = rowData.type === SegmentTableItemType.FOLDER ? folderMenuItems : segmentMenuItems;

    if (!menuItems?.length) {
        return null;
    }

    return <MoreOptionsDropdown menuItems={menuItems} />;
};

export default memo(SegmentsTableOptionsCell);
